import logger from '../../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { GlobalStateContext } from '../../utils/GlobalStateContext';
import { getUserId } from '../../utils/utils';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../style/ImagingModals.css';
import '../../style/OrderDisplay.css';
import useImagingCatalog from '../../hooks/useImagingCatalog';

const modalities = ['X-Ray', 'DEXA', 'MRI', 'CT', 'US'];

const regionBodyPartsMap = {
  'Head & Neck': ['Brain', 'Face', 'Orbit', 'Sinuses', 'Neck (Soft Tissue)', 'Ear', 'Throat', 'Carotid Arteries', 'Orbits'],
  'Spine': ['Cervical Spine', 'Thoracic Spine', 'Lumbar Spine', 'Sacrum', 'Coccyx'],
  'Upper Extremity': ['Shoulder', 'Humerus', 'Elbow', 'Forearm', 'Wrist', 'Hand', 'Fingers'],
  'Lower Extremity': ['Hip', 'Femur', 'Knee', 'Tibia/Fibula', 'Ankle', 'Foot', 'Toes', 'Legs', 'Venous Doppler Lower Extremities'],
  'Chest / Thorax': ['Chest', 'Lungs', 'Heart', 'Ribs', 'Sternum', 'Thoracic Aorta'],
  'Abdomen / Pelvis': ['Liver', 'Kidneys', 'Bladder', 'Pancreas', 'Pelvis', 'Appendix', 'GI Tract', 'Abdomen'],
  'Vascular': ['Carotid Arteries', 'Abdominal Aorta', 'Peripheral Arteries', 'Renal Arteries'],
  'Soft Tissue / Foreign Body': ['Extremities', 'Neck', 'Soft Tissue', 'Foreign Body'],
  'Full Body': ['Full Body MRI'],
};

const anatomicalMap = {
  'Head & Neck': [
    { id: 1, code: '70450', name: 'CT head (non-contrast)', modality: 'CT' },
    { id: 2, code: '70470', name: 'CT head w/o and w/ contrast', modality: 'CT' },
    { id: 3, code: '70471', name: 'CT Angiogram (CTA) Head and Neck', modality: 'CT' },
    { id: 4, code: '70551', name: 'MRI Brain w/o contrast', modality: 'MRI' },
    { id: 5, code: '70552', name: 'MRI Brain w/o and w/ contrast', modality: 'MRI' },
    { id: 6, code: '70553', name: 'MRI Brain w/o and w/ Pituitary Protocol', modality: 'MRI' },
    { id: 7, code: '70553', name: 'MRI Brain w/o and w/ Epilepsy Protocol', modality: 'MRI' },
    { id: 8, code: '70544', name: 'MRA Head w/o and w/ contrast', modality: 'MRI' },
    { id: 9, code: '70544', name: 'MRA Circle of Willis', modality: 'MRI' },
    { id: 10, code: '70260', name: 'Skull (4 views)', modality: 'X-Ray' },
    { id: 11, code: '77085', name: 'DEXA Scan', modality: 'DEXA' },
    { id: 12, code: '70486', name: 'Facial CT w/o contrast', modality: 'CT' },
    { id: 13, code: '70487', name: 'Facial CT w/ contrast', modality: 'CT' },
    { id: 14, code: '70488', name: 'Facial CT w/o and w/ contrast', modality: 'CT' },
    { id: 15, code: '70150', name: 'Facial Bones (3 views)', modality: 'X-Ray' },
    { id: 16, code: '76536', name: 'Ultrasound Parotid Glands Left', modality: 'US' },
    { id: 17, code: '76536', name: 'Ultrasound Parotid Glands Right', modality: 'US' },
    { id: 18, code: '76536', name: 'Ultrasound Parotid Glands Both', modality: 'US' },
    { id: 19, code: '70486', name: 'CT sinus w/o contrast', modality: 'CT' },
    { id: 20, code: '70487', name: 'CT sinus w/ contrast', modality: 'CT' },
    { id: 21, code: '70488', name: 'CT sinus w/o and w/ contrast', modality: 'CT' },
    { id: 22, code: '70540', name: 'MRI Sinuses w/o contrast', modality: 'MRI' },
    { id: 23, code: '70540', name: 'MRI Sinuses w/o and w/ contrast', modality: 'MRI' },
    { id: 24, code: '70220', name: 'Sinuses (Waters view)', modality: 'X-Ray' },
    { id: 25, code: '70160', name: 'Nasal Bones (2-3 views)', modality: 'X-Ray' },
    { id: 26, code: '70498', name: 'CT Angiogram (CTA) Neck', modality: 'CT' },
    { id: 27, code: '70490', name: 'CT Neck w/o contrast', modality: 'CT' },
    { id: 28, code: '70491', name: 'CT Neck w/ contrast', modality: 'CT' },
    { id: 29, code: '70492', name: 'CT Neck w/o and w/ contrast', modality: 'CT' },
    { id: 30, code: '70547', name: 'MRA Neck w/o and w/ contrast', modality: 'MRI' },
    { id: 31, code: '76536', name: 'Ultrasound thyroid', modality: 'US' },
    { id: 32, code: '76536', name: 'Ultrasound neck', modality: 'US' },
    { id: 33, code: '76536', name: 'Ultrasound Submandibular Glands Left', modality: 'US' },
    { id: 34, code: '76536', name: 'Ultrasound Submandibular Glands Right', modality: 'US' },
    { id: 35, code: '76536', name: 'Ultrasound Submandibular Glands Both', modality: 'US' },
  ],
  'Spine': [
    { id: 39, code: '72125', name: 'CT Cervical Spine w/o Contrast', modality: 'CT' },
    { id: 40, code: '72126', name: 'CT Cervical Spine w/ Contrast', modality: 'CT' },
    { id: 41, code: '72127', name: 'CT Cervical Spine w/o and w/ Contrast', modality: 'CT' },
    { id: 42, code: '72141', name: 'MRI Cervical Spine w/o contrast', modality: 'MRI' },
    { id: 43, code: '72156', name: 'MRI Cervical Spine w/o and w/ contrast', modality: 'MRI' },
    { id: 44, code: '72040', name: 'Cervical Spine (2-3 views)', modality: 'X-Ray' },
    { id: 45, code: '72128', name: 'CT Thoracic Spine w/o contrast', modality: 'CT' },
    { id: 46, code: '72129', name: 'CT Thoracic Spine w/ contrast', modality: 'CT' },
    { id: 47, code: '72130', name: 'CT Thoracic Spine w/o and w/ contrast', modality: 'CT' },
    { id: 48, code: '72146', name: 'MRI Thoracic Spine w/o contrast', modality: 'MRI' },
    { id: 49, code: '72157', name: 'MRI Thoracic Spine w/o and w/ contrast', modality: 'MRI' },
    { id: 50, code: '72070', name: 'Thoracic Spine (2 views)', modality: 'X-Ray' },
    { id: 51, code: '72082', name: 'Scoliosis Series (Ap and lateral)', modality: 'X-Ray' },
    { id: 52, code: '72131', name: 'CT Lumbar Spine w/o contrast', modality: 'CT' },
    { id: 53, code: '72132', name: 'CT Lumbar Spine w/ contrast', modality: 'CT' },
    { id: 54, code: '72133', name: 'CT Lumbar Spine w/o and w/ contrast', modality: 'CT' },
    { id: 55, code: '72148', name: 'MRI Lumbar Spine w/o contrast', modality: 'MRI' },
    { id: 56, code: '72158', name: 'MRI Lumbar Spine w/o and w/ contrast', modality: 'MRI' },
    { id: 57, code: '72100', name: 'Lumbar Spine (2-3 views)', modality: 'X-Ray' },
    { id: 58, code: '72133', name: 'CT Sacrum w/o and w/ contrast', modality: 'CT' },
    { id: 59, code: '72195', name: 'MRI Sacrum/Sacroiliac Joints w/ contrast', modality: 'MRI' },
    { id: 60, code: '72196', name: 'MRI Sacrum/Sacroiliac Joints w/o contrast', modality: 'MRI' },
    { id: 61, code: '72197', name: 'MRI Sacrum/Sacroiliac Joints w/o and w/ contrast', modality: 'MRI' },
    { id: 62, code: '72220', name: 'Sacrum/Coccyx (2 views)', modality: 'X-Ray' },
    { id: 63, code: '77085', name: 'DEXA Scan', modality: 'DEXA' },
  ],
  'Upper Extremity': [
    { id: 74, code: '73200', name: 'CT Left Shoulder w/o contrast', modality: 'CT' },
    { id: 75, code: '73200', name: 'CT Right Shoulder w/o contrast', modality: 'CT' },
    { id: 76, code: '73201', name: 'CT Left Shoulder w/ contrast', modality: 'CT' },
    { id: 77, code: '73201', name: 'CT Right Shoulder w/ contrast', modality: 'CT' },
    { id: 78, code: '73202', name: 'CT Left Shoulder w/o and w/ contrast', modality: 'CT' },
    { id: 79, code: '73202', name: 'CT Right Shoulder w/o and w/ contrast', modality: 'CT' },
    { id: 80, code: '73221', name: 'MRI Left Shoulder w/o contrast', modality: 'MRI' },
    { id: 81, code: '73221', name: 'MRI Right Shoulder w/o contrast', modality: 'MRI' },
    { id: 82, code: '73223', name: 'MRI Left Shoulder w/o and w/ contrast', modality: 'MRI' },
    { id: 83, code: '73223', name: 'MRI Right Shoulder w/o and w/ contrast', modality: 'MRI' },
    { id: 84, code: '73030', name: 'Left Shoulder 2 view', modality: 'X-Ray' },
    { id: 85, code: '73030', name: 'Right Shoulder 2 view', modality: 'X-Ray' },
    { id: 86, code: '73030', name: 'Both Shoulders 2 view', modality: 'X-Ray' },
    { id: 87, code: '73000', name: 'Left Clavicle (2 views)', modality: 'X-Ray' },
    { id: 88, code: '73000', name: 'Right Clavicle (2 views)', modality: 'X-Ray' },
    { id: 89, code: '73010', name: 'Left Scapula (AP and lateral)', modality: 'X-Ray' },
    { id: 90, code: '73010', name: 'Right Scapula (AP and lateral)', modality: 'X-Ray' },
    { id: 91, code: '76881', name: 'Ultrasound Shoulder Left Complete', modality: 'US' },
    { id: 92, code: '76881', name: 'Ultrasound Shoulder Right Complete', modality: 'US' },
    { id: 93, code: '76882', name: 'Ultrasound Shoulder Left Limited', modality: 'US' },
    { id: 94, code: '76882', name: 'Ultrasound Shoulder Right Limited', modality: 'US' },
    { id: 95, code: '73200', name: 'CT Left Elbow w/o contrast', modality: 'CT' },
    { id: 96, code: '73200', name: 'CT Right Elbow w/o contrast', modality: 'CT' },
    { id: 97, code: '73221', name: 'MRI Left Elbow w/o contrast', modality: 'MRI' },
    { id: 98, code: '73221', name: 'MRI Right Elbow w/o contrast', modality: 'MRI' },
    { id: 99, code: '73080', name: 'Left Elbow AP and Lateral', modality: 'X-Ray' },
    { id: 100, code: '73080', name: 'Right Elbow AP and Lateral', modality: 'X-Ray' },
    { id: 101, code: '73200', name: 'CT Left Wrist w/o contrast', modality: 'CT' },
    { id: 102, code: '73200', name: 'CT Right Wrist w/o contrast', modality: 'CT' },
    { id: 103, code: '73221', name: 'MRI Left Wrist w/o contrast', modality: 'MRI' },
    { id: 104, code: '73221', name: 'MRI Right Wrist w/o contrast', modality: 'MRI' },
    { id: 105, code: '73110', name: 'Left Wrist (3 views)', modality: 'X-Ray' },
    { id: 106, code: '73110', name: 'Right Wrist (3 views)', modality: 'X-Ray' },
    { id: 107, code: '73200', name: 'CT Left Hand w/o contrast', modality: 'CT' },
    { id: 108, code: '73200', name: 'CT Right Hand w/o contrast', modality: 'CT' },
    { id: 109, code: '73221', name: 'MRI Left Hand w/o contrast', modality: 'MRI' },
    { id: 110, code: '73221', name: 'MRI Right Hand w/o contrast', modality: 'MRI' },
    { id: 111, code: '73130', name: 'Left Hand (3 views)', modality: 'X-Ray' },
    { id: 112, code: '73130', name: 'Right Hand (3 views)', modality: 'X-Ray' },
    { id: 113, code: '73140', name: 'Left Fingers 1-5 (2-3 views)', modality: 'X-Ray' },
    { id: 114, code: '73140', name: 'Right Fingers 1-5 (2-3 views)', modality: 'X-Ray' },
    { id: 115, code: '77085', name: 'DEXA Scan Upper', modality: 'DEXA' },
  ],
  'Lower Extremity': [
    { id: 240, code: '73700', name: 'MRI Left Hip w/o contrast', modality: 'MRI' },
    { id: 241, code: '73700', name: 'MRI Right Hip w/o contrast', modality: 'MRI' },
    { id: 242, code: '73700', name: 'CT Left Hip w/o contrast', modality: 'CT' },
    { id: 243, code: '73700', name: 'CT Right Hip w/o contrast', modality: 'CT' },
    { id: 244, code: '73702', name: 'MRI Left Hip w/o and w/ contrast', modality: 'MRI' },
    { id: 245, code: '73702', name: 'MRI Right Hip w/o and w/ contrast', modality: 'MRI' },
    { id: 246, code: '73502', name: 'Left Hip (AP and lateral, 2 views)', modality: 'X-Ray' },
    { id: 247, code: '73502', name: 'Right Hip (AP and lateral, 2 views)', modality: 'X-Ray' },
    { id: 248, code: '73700', name: 'MRI Left Knee w/o contrast', modality: 'MRI' },
    { id: 249, code: '73700', name: 'MRI Right Knee w/o contrast', modality: 'MRI' },
    { id: 250, code: '73700', name: 'CT Left Knee w/o contrast', modality: 'CT' },
    { id: 251, code: '73700', name: 'CT Right Knee w/o contrast', modality: 'CT' },
    { id: 252, code: '73702', name: 'MRI Left Knee w/o and w/ contrast', modality: 'MRI' },
    { id: 253, code: '73702', name: 'MRI Right Knee w/o and w/ contrast', modality: 'MRI' },
    { id: 254, code: '73562', name: 'Left Knee (3-4 views)', modality: 'X-Ray' },
    { id: 255, code: '73562', name: 'Right Knee (3-4 views)', modality: 'X-Ray' },
    { id: 256, code: '76881', name: 'Ultrasound Knee Left Complete', modality: 'US' },
    { id: 257, code: '76881', name: 'Ultrasound Knee Right Complete', modality: 'US' },
    { id: 258, code: '73590', name: 'Left Tibia/Fibula AP and Lateral', modality: 'X-Ray' },
    { id: 259, code: '73590', name: 'Right Tibia/Fibula AP and Lateral', modality: 'X-Ray' },
    { id: 260, code: '73700', name: 'MRI Left Ankle w/o contrast', modality: 'MRI' },
    { id: 261, code: '73700', name: 'MRI Right Ankle w/o contrast', modality: 'MRI' },
    { id: 262, code: '73700', name: 'CT Left Ankle w/o contrast', modality: 'CT' },
    { id: 263, code: '73700', name: 'CT Right Ankle w/o contrast', modality: 'CT' },
    { id: 264, code: '73610', name: 'Left Ankle (3 Views)', modality: 'X-Ray' },
    { id: 265, code: '73610', name: 'Right Ankle (3 Views)', modality: 'X-Ray' },
    { id: 266, code: '73700', name: 'MRI Left Foot w/o contrast', modality: 'MRI' },
    { id: 267, code: '73700', name: 'MRI Right Foot w/o contrast', modality: 'MRI' },
    { id: 268, code: '73700', name: 'CT Left Foot w/o contrast', modality: 'CT' },
    { id: 269, code: '73700', name: 'CT Right Foot w/o contrast', modality: 'CT' },
    { id: 270, code: '73630', name: 'Left Foot (3 views)', modality: 'X-Ray' },
    { id: 271, code: '73630', name: 'Right Foot (3 views)', modality: 'X-Ray' },
    { id: 272, code: '73660', name: 'Left Toes 1-5 (2-3 views)', modality: 'X-Ray' },
    { id: 273, code: '73660', name: 'Right Toes 1-5 (2-3 views)', modality: 'X-Ray' },
    { id: 274, code: '93970', name: 'Ultrasound Right Lower Extremity Venous Doppler', modality: 'US' },
    { id: 275, code: '93970', name: 'Ultrasound Left Lower Extremity Venous Doppler', modality: 'US' },
    { id: 276, code: '93925', name: 'Ultrasound Right Lower Extremity Arterial Doppler', modality: 'US' },
    { id: 277, code: '93925', name: 'Ultrasound Left Lower Extremity Arterial Doppler', modality: 'US' },
    { id: 278, code: '77085', name: 'DEXA Scan Lower', modality: 'DEXA' },
  ],
  'Chest / Thorax': [
    { id: 349, code: '71550', name: 'Chest MRI w/o contrast', modality: 'MRI' },
    { id: 350, code: '71551', name: 'Chest MRI w/ contrast', modality: 'MRI' },
    { id: 351, code: '71552', name: 'Chest MRI w/o and w/ contrast', modality: 'MRI' },
    { id: 352, code: '71250', name: 'CT chest w/o contrast', modality: 'CT' },
    { id: 353, code: '71260', name: 'CT chest with contrast', modality: 'CT' },
    { id: 354, code: '71275', name: 'CT Pulmonary Angiogram (CTPA)', modality: 'CT' },
    { id: 355, code: '71046', name: 'Chest X-Ray (PA and lateral)', modality: 'X-Ray' },
    { id: 356, code: '71045', name: 'Chest X-Ray (portable AP)', modality: 'X-Ray' },
    { id: 357, code: '75571', name: 'CT Coronary Calcium Score', modality: 'CT' },
    { id: 358, code: '75574', name: 'CT Coronary Angiogram (CTA Coronary)', modality: 'CT' },
    { id: 359, code: '93660', name: 'Echocardiogram (Transthoracic)', modality: 'US' },
    { id: 360, code: '71100', name: 'Unilateral ribs (2 views)', modality: 'X-Ray' },
    { id: 361, code: '71110', name: 'Bilateral ribs (3 views)', modality: 'X-Ray' },
    { id: 362, code: '71120', name: 'Sternum (lateral and oblique)', modality: 'X-Ray' },
    { id: 363, code: '71555', name: 'MRA Chest (aorta protocol)', modality: 'MRI' },
    { id: 364, code: '76705', name: 'Aorta/IVC', modality: 'US' },
    { id: 365, code: '77085', name: 'DEXA Scan', modality: 'DEXA' },
  ],
  'Abdomen / Pelvis': [
    { id: 385, code: '74177', name: 'CT Abdomen & Pelvis w/ contrast', modality: 'CT' },
    { id: 386, code: '74176', name: 'CT Abdomen & Pelvis w/o contrast', modality: 'CT' },
    { id: 387, code: '74183', name: 'MRI Liver w/o and w/ contrast', modality: 'MRI' },
    { id: 388, code: '74183', name: 'MRI Pancreas w/o and w/ contrast', modality: 'MRI' },
    { id: 389, code: '74019', name: 'Abdomen (upright and supine, 2 views)', modality: 'X-Ray' },
    { id: 390, code: '74018', name: 'Abdomen (KUB, 1 view)', modality: 'X-Ray' },
    { id: 391, code: '76700', name: 'Ultrasound Abdomen Complete', modality: 'US' },
    { id: 392, code: '76705', name: 'Ultrasound Abdomen Limited', modality: 'US' },
    { id: 393, code: '76770', name: 'Renal/Bladder Complete', modality: 'US' },
    { id: 394, code: '74176', name: 'CT Urogram w/o Contrast', modality: 'CT' },
    { id: 395, code: '74177', name: 'CT Urogram w/ Contrast', modality: 'CT' },
    { id: 396, code: '74178', name: 'CT Urogram w/o and w/ Contrast', modality: 'CT' },
    { id: 397, code: '72192', name: 'CT Pelvis w/o contrast', modality: 'CT' },
    { id: 398, code: '74174', name: 'CT Angiogram (Abdomen/Pelvis)', modality: 'CT' },
    { id: 399, code: '75635', name: 'CT Angiogram (Runoff)', modality: 'CT' },
    { id: 400, code: '72197', name: 'MRI Pelvis w/o contrast', modality: 'MRI' },
    { id: 401, code: '72197', name: 'MRI Pelvis w/o and w/ contrast', modality: 'MRI' },
    { id: 402, code: '72197', name: 'MRI Prostate (multiparametric)', modality: 'MRI' },
    { id: 403, code: '72195', name: 'MRI Female Pelvis w/o contrast', modality: 'MRI' },
    { id: 404, code: '72197', name: 'MRI Female Pelvis w/o and w/ contrast', modality: 'MRI' },
    { id: 405, code: '72170', name: 'Pelvis 2 view', modality: 'X-Ray' },
    { id: 406, code: '76856', name: 'Ultrasound Pelvis (transabdominal)', modality: 'US' },
    { id: 407, code: '76830', name: 'Ultrasound Pelvis (transvaginal)', modality: 'US' },
    { id: 408, code: '76870', name: 'Ultrasound Scrotum/Testicles', modality: 'US' },
    { id: 409, code: '76705', name: 'Ultrasound Gallbladder', modality: 'US' },
    { id: 410, code: '76705', name: 'Ultrasound Liver Elastography', modality: 'US' },
    { id: 411, code: '76815', name: 'Ultrasound OB Limited', modality: 'US' },
    { id: 412, code: '76805', name: 'Ultrasound OB >14 Weeks', modality: 'US' },
    { id: 413, code: '76801', name: 'Ultrasound OB <14 Weeks', modality: 'US' },
    { id: 414, code: '77085', name: 'DEXA Scan', modality: 'DEXA' },
  ],
  'Vascular': [
    { id: 451, code: '93880', name: 'Ultrasound Carotid Duplex (bilateral)', modality: 'US' },
    { id: 452, code: '74185', name: 'MRA Abdomen (aorta or renal arteries)', modality: 'MRI' },
    { id: 453, code: '76706', name: 'Ultrasound Aorta (AAA screening)', modality: 'US' },
    { id: 454, code: '93926', name: 'Ultrasound Left arterial duplex LE Limited', modality: 'US' },
    { id: 455, code: '93925', name: 'Ultrasound Left arterial duplex LE', modality: 'US' },
    { id: 456, code: '93931', name: 'Ultrasound Left arterial duplex UE Limited', modality: 'US' },
    { id: 457, code: '93930', name: 'Ultrasound Left arterial duplex UE', modality: 'US' },
    { id: 458, code: '93970', name: 'Ultrasound Left duplex veins LE', modality: 'US' },
  ],
  'Soft Tissue / Foreign Body': [
    { id: 460, code: '76536', name: 'Ultrasound Soft Tissue', modality: 'US' },
    { id: 461, code: '73725', name: 'MRA Lower Extremities (runoff) Left', modality: 'MRI' },
    { id: 462, code: '73725', name: 'MRA Lower Extremities (runoff) Right', modality: 'MRI' },
    { id: 463, code: '76881', name: 'Ultrasound Upper Extremities Left Complete', modality: 'US' },
    { id: 464, code: '76881', name: 'Ultrasound Upper Extremities Right Complete', modality: 'US' },
    { id: 465, code: '76881', name: 'Ultrasound Lower Extremities Left Complete', modality: 'US' },
    { id: 466, code: '76881', name: 'Ultrasound Lower Extremities Right Complete', modality: 'US' },
    { id: 467, code: '74183', name: 'MRI Abdomen w/o contrast', modality: 'MRI' },
    { id: 468, code: '74183', name: 'MRI Abdomen w/o and w/ contrast', modality: 'MRI' },
    { id: 469, code: '77081', name: 'Appendicular Upper Extremities DEXA', modality: 'DEXA' },
    { id: 470, code: '77081', name: 'Appendicular Lower Extremities DEXA', modality: 'DEXA' },
    { id: 471, code: '76010', name: 'X-Ray Foreign Body Localization', modality: 'X-Ray' },
  ],
  'Full Body': [
    { id: 515, code: '77084', name: 'MRI Full Body Screening', modality: 'MRI' },
    { id: 516, code: '76499', name: 'DEXA Full Body Screening', modality: 'DEXA' },
  ],
};

const RISOrderImagingModal = ({ isOpen, onClose, onOrderPlaced, followupNoteId, risPatientId }) => {
  const { currentUser } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const user = getUserId(currentUser);

  const [modality, setModality] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedBodyPart, setSelectedBodyPart] = useState('');
  const [cptCode, setCptCode] = useState('');
  const [examOrdered, setExamOrdered] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [cptCodesFiltered, setCptCodesFiltered] = useState([]);
  const [priority, setPriority] = useState('Routine');
  const [options, setOptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [clinicalHistory, setClinicalHistory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mergedAnatomicalMap, mergedRegionBodyPartsMap } = useImagingCatalog(anatomicalMap, regionBodyPartsMap);

  useEffect(() => {
    if (!selectedRegion || !modality) {
      setCptCodesFiltered([]);
      setCptCode('');
      setExamOrdered('');
      setSelectedItemId('');
      return;
    }
    const filtered = (mergedAnatomicalMap[selectedRegion] || []).filter(item => item.modality === modality);
    setCptCodesFiltered(filtered);
    setCptCode('');
    setExamOrdered('');
    setSelectedItemId('');
  }, [selectedRegion, modality, mergedAnatomicalMap]);

  const handleOptionsChange = (e) => {
    const { value, checked } = e.target;
    setOptions(prev => checked ? [...prev, value] : prev.filter(o => o !== value));
  };

  const resetForm = () => {
    setModality('');
    setSelectedRegion('');
    setSelectedBodyPart('');
    setCptCode('');
    setExamOrdered('');
    setSelectedItemId('');
    setNotes('');
    setPriority('Routine');
    setOptions([]);
    setClinicalHistory('');
    setCptCodesFiltered([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!modality) { toast.warning('Please select a modality'); return; }
    if (!selectedRegion) { toast.warning('Please select a region'); return; }
    if (!cptCode) { toast.warning('Please select a CPT code'); return; }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/followUpRadOrder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          followup_note_id: followupNoteId,
          ris_patients_id: risPatientId,
          modality,
          region: selectedRegion,
          body_part: selectedBodyPart,
          cpt_code: cptCode,
          exam_ordered: examOrdered,
          priority,
          clinical_history: clinicalHistory,
          notes,
          options: options.join(', '),
          user,
          type: currentUser.type,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.msg || 'Radiology order submitted successfully');
        resetForm();
        if (onOrderPlaced) await onOrderPlaced();
      } else {
        toast.error(data.msg || 'Failed to submit radiology order');
      }
    } catch (error) {
      logger.error('Error submitting radiology order:', error);
      toast.error('Error submitting radiology order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="encounterOrderModal-backdrop" onClick={onClose}>
      <div className="encounterOrderModal-modal" onClick={e => e.stopPropagation()}>
        <h2 className="encounterOrderModal-title">Order Imaging</h2>
        <button className="encounterOrderModal-close" onClick={onClose}>×</button>

        <div className="modality-container">
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label className="label-field">Modality:</label>
              <select className="select-field" value={modality} onChange={e => { setModality(e.target.value); setSelectedBodyPart(''); }} disabled={isSubmitting}>
                <option value="">Select Modality</option>
                {modalities.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="label-field">Region:</label>
              <select className="select-field" value={selectedRegion} onChange={e => { setSelectedRegion(e.target.value); setSelectedBodyPart(''); setCptCode(''); }} disabled={!modality || isSubmitting}>
                <option value="">Select Region</option>
                {Object.keys(anatomicalMap).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="label-field">Body Part:</label>
              <select className="select-field" value={selectedBodyPart} onChange={e => setSelectedBodyPart(e.target.value)} disabled={!selectedRegion || isSubmitting}>
                <option value="">Select Body Part</option>
                {(mergedRegionBodyPartsMap[selectedRegion] || []).map(bp => <option key={bp} value={bp}>{bp}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="label-field">CPT Code:</label>
              <select
                className="select-field"
                value={selectedItemId}
                onChange={e => {
                  const selected = cptCodesFiltered.find(c => String(c.id) === e.target.value);
                  if (selected) {
                    setSelectedItemId(e.target.value);
                    setCptCode(selected.code);
                    setExamOrdered(selected.name);
                  }
                }}
                disabled={!cptCodesFiltered.length || isSubmitting}
              >
                <option value="">Select CPT Code</option>
                {cptCodesFiltered.map(({ id, code, name }) => (
                  <option key={id} value={id}>{code} – {name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label-field">Priority:</label>
              <select className="select-field" value={priority} onChange={e => setPriority(e.target.value)} disabled={isSubmitting}>
                <option value="Routine">Routine</option>
                <option value="STAT">STAT</option>
                <option value="Stroke">Stroke</option>
                <option value="Trauma">Trauma</option>
                <option value="Next Day">Next Day</option>
                <option value="2 Day">2 Day</option>
              </select>
            </div>

            <fieldset className="options" disabled={isSubmitting}>
              <legend>Additional Options</legend>
              {['IV Gadolinium', 'IV Contrast', 'Oral Contrast', 'Enteric Contrast', 'Sedation', 'Fluids'].map(opt => (
                <label key={opt}>
                  <input type="checkbox" value={opt} checked={options.includes(opt)} onChange={handleOptionsChange} disabled={isSubmitting} />
                  {opt}
                </label>
              ))}
            </fieldset>

            <div className="field">
              <label className="label-field">Clinical History:</label>
              <textarea value={clinicalHistory} onChange={e => setClinicalHistory(e.target.value)} className="imaging-order-notes" rows={4} disabled={isSubmitting} />
            </div>

            <div className="field">
              <label className="label-field">Notes:</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className="imaging-order-notes" disabled={isSubmitting} />
            </div>

            <button type="submit" className="PrimaryButton" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Imaging Order'}
            </button>
          </form>
        </div>

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      </div>
    </div>
  );
};

export default RISOrderImagingModal;
